// The sums behind the Property lookup page (property/PropertyLookup.js): EPC bands,
// certificate validity, postcodes in links, and the home's estimated value over time.

import { AREA_REPORT_PATH, areaReportPath } from "../areareport/areaReportRules";

export const PROPERTY_PATH = '/property';

// The standard EPC bands, A (most efficient) to G, with their score ranges and colours
// (the same colours as the sale popups, Layers/PropertySalesLayer.js)
export const BANDS = [
    {band:'A', lo:92, hi:100, bg:'#008054', fg:'#ffffff'},
    {band:'B', lo:81, hi:91, bg:'#19b459', fg:'#ffffff'},
    {band:'C', lo:69, hi:80, bg:'#8cc63f', fg:'#173000'},
    {band:'D', lo:55, hi:68, bg:'#ffd500', fg:'#3d3300'},
    {band:'E', lo:39, hi:54, bg:'#fcaa65', fg:'#4a2400'},
    {band:'F', lo:21, hi:38, bg:'#ef8023', fg:'#3d1c00'},
    {band:'G', lo:1, hi:20, bg:'#e9153b', fg:'#ffffff'},
];
export const bandOf = (b) => BANDS.find((x) => x.band === b) || {band:b || '?', bg:'#dde4e1', fg:'#14231f'};

// A full UK postcode tidied to "B32 3NH", or null
export const normalisePostcode = (s) => {
    const t = (s || '').toUpperCase().replace(/\s+/g, '');
    return /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/.test(t) ? t.slice(0, -3) + ' ' + t.slice(-3) : null;
};
// Postcodes in page links: "B32 3NH" <-> "B32-3NH"
export const postcodeToSlug = (pc) => pc.replace(' ', '-');
export const slugToPostcode = (slug) => normalisePostcode((slug || '').replace(/-/g, ' '));

export const homesPath = (pc) => PROPERTY_PATH + '/' + postcodeToSlug(pc);
export const propertyPath = (pc, uprn) => homesPath(pc) + '/' + uprn;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const parseDate = (s) => new Date(s + 'T00:00:00');
export const fmtDate = (s) => { const d = parseDate(s); return d.getDate() + ' ' + MONTHS[d.getMonth()] + ' ' + d.getFullYear(); };
export const fmtMonth = (d) => MONTHS[d.getMonth()] + ' ' + d.getFullYear();
export const money = (n) => '£' + Math.round(n).toLocaleString('en-GB');
// Estimates are shown to the nearest £1,000
export const roundEstimate = (n) => Math.round(n / 1000) * 1000;

// An EPC is valid for ten years. The certificates carry their inspection date, not the
// date they were lodged, so this counts from the inspection.
export const validUntil = (s) => { const d = parseDate(s); d.setFullYear(d.getFullYear() + 10); return d; };
export const isExpired = (s, today = new Date()) => validUntil(s) < today;

// "England and Wales: 1967-1975" -> "1967-1975"; "England and Wales: before 1900" -> "Before 1900"
export const ageBand = (s) => {
    if (!s) return null;
    const t = s.replace(/^.*?:\s*/, '');
    return t.charAt(0).toUpperCase() + t.slice(1);
};

// Quarters as salesseries_oa keys them: "2024-3"
export const quarterOf = (s) => { const d = parseDate(s); return d.getFullYear() + '-' + (Math.floor(d.getMonth() / 3) + 1); };
const quarterIndex = (q) => { const [y, n] = q.split('-').map(Number); return y * 4 + n - 1; };
export const quarterLabel = (q) => { const [y, n] = q.split('-'); return ['Jan', 'Apr', 'Jul', 'Oct'][n - 1] + ' ' + y; };
// The months a quarter covers: "2024-3" -> "Jul–Sep 2024"
export const quarterPeriod = (q) => { const [y, n] = q.split('-'); return ['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Oct–Dec'][n - 1] + ' ' + y; };

// Whether the home is valued as a flat: by the type of its latest sale (property.proptype,
// from ukmap.get_property, which counts category B sales too) when it has sold, else by
// its latest energy certificate
const isFlat = (property) => {
    if (property.proptype) return property.proptype === 'F';
    const sale = property.sales.length ? property.sales[property.sales.length - 1] : null;
    if (sale) return sale.proptype === 'F';
    const cert = property.certs[property.certs.length - 1];
    return /flat|maisonette/i.test(cert.ptype || '');
};

// The index the value is moved through time by, each quarter of used, as ukmap.value_index:
// the geometric mean of the area's price per m² (price, averaged over the year to the quarter,
// as a quarter of few sales can swing it by a third) and the region's (rppsm, from
// ukmap.salesseries_region: {"2024-3": {"Houses": n, "Flats": n, "All": n}}). Moving a home's
// earlier sale by this was out by 9.3% (median) for flats and 7.8% for houses on 2026 sales,
// against 10.5% and 8.3% by the area's quarter alone. With no region, the area's quarter.
export const valueIndex = (used, price, rppsm, kind) => {
    const idx = {};
    if (!rppsm) { used.forEach((q) => { idx[q] = price[q]; }); return idx; }
    const reg = used.map((q) => { const r = rppsm[q] || {}; return Number(r[kind] || r.All) || null; });
    let known = reg.find((v) => v) || null;
    used.forEach((q, i) => {
        if (reg[i]) known = reg[i];
        const year = used.slice(Math.max(0, i - 3), i + 1).map((y) => price[y]);
        const local = year.reduce((a, b) => a + b, 0) / year.length;
        idx[q] = known ? Math.sqrt(local * known) : price[q];
    });
    return idx;
};

// A sale's weight in the blend with the comparables, by years since it: about 0.6 after 5
// years, 0.37 after 10 (tested in sql/value_model_backtest.sql)
export const SALE_WEIGHT_YEARS = 10;

// The home's estimated value each quarter. The shape comes from the median price per m² of
// homes like it nearby (ppsm, ukmap.salesseries_oa: {"2024-3": {"Houses": n, "Flats": n,
// "All": n}}; a quarter with none for its kind uses All, then the last known price), the level
// from the comparable sales model (model, ukmap.property_comps: the price per m² of homes like
// it nearby in quarter model.q, with the likely range lo to hi) and from its own sales:
// - never sold, or only below market: size × the model's price per m², moved back by the area
//   price each quarter (size × the area price when there's no model)
// - before its first sale: that sale price moved back by the area price
// - between two sales: the first moved by the area price, with the gap to the second spread
//   evenly over the quarters between, so the line meets each sale without a jump
// (Sales and the model's value are moved between quarters by valueIndex, the area price
// steadied by the region's, while ppsmNow and plain stay the area's own price this quarter.)
// - after its last sale: that sale moved by the area price, blended with the model's value,
//   the sale counting less the older it is (exp(-years/SALE_WEIGHT_YEARS)); with no model,
//   the sale moved by the area price alone
// Sales marked belowmarket (under 0.6× the model's value at the time, most often a shared
// ownership share; ukmap.get_property) don't set the value. Size is its latest EPC's floor area,
// else the one matched to its latest sale (ukmap.pxepc). Returns null when there's no size or
// no prices. The last point is the value today.
// size, when given, is the floor area to value instead of the home's recorded one (a size
// from a listing, or typed in by the user on the listing page)
export const valueSeries = (property, sizeOverride) => {
    const {sales, certs, ppsm, model} = property;
    if (!ppsm || !certs || !certs.length) return null;
    const lastSale = sales.length ? sales[sales.length - 1] : null;
    const override = Number(sizeOverride) > 0 ? Number(sizeOverride) : null;
    // The latest certificate's floor area, which is newer than the one matched to an older sale
    const certArea = Number(certs[certs.length - 1].area) || null;
    const size = override || certArea || Number(lastSale && lastSale.area);
    if (!size) return null;
    const flat = isFlat(property);
    const kind = flat ? 'Flats' : 'Houses';

    const quarters = Object.keys(ppsm).sort((a, b) => quarterIndex(a) - quarterIndex(b));
    if (!quarters.length) return null;
    let known = null;
    const price = {};
    quarters.forEach((q) => {
        const p = ppsm[q] || {};
        const v = Number(p[kind] || p.All) || known;
        if (v) known = v;
        price[q] = v;
    });
    const first = quarters.find((q) => price[q]);
    if (!first) return null;
    const used = quarters.slice(quarters.indexOf(first));
    const lastQ = used[used.length - 1];
    const idx = valueIndex(used, price, property.rppsm, kind);

    // The model's value for the home each quarter, moved by the area price from its quarter
    const modelPpsm = model && Number(model.ppsm) > 0 ? Number(model.ppsm) : null;
    const modelQ = modelPpsm ? (price[model.q] ? model.q : lastQ) : null;
    const base = (q) => modelPpsm ? modelPpsm * size * idx[q] / idx[modelQ] : size * price[q];

    // The latest sale in each quarter, and the quarters with one
    const saleIn = {};
    sales.filter((s) => !s.belowmarket).forEach((s) => { saleIn[quarterOf(s.date)] = s; });
    const anchors = used.map((q, i) => saleIn[q] ? {i, q, v:Number(saleIn[q].price)} : null).filter(Boolean);

    // The sale moved by the index from its quarter to q
    const moved = (a, q) => a.v * idx[q] / idx[a.q];
    let saleWeight = null;
    const points = used.map((q, i) => {
        const sale = saleIn[q] || null;
        if (!anchors.length) return {q, v:base(q), sale};
        const next = anchors.findIndex((a) => a.i >= i);
        if (next === 0) return {q, v:moved(anchors[0], q), sale};
        if (next > 0) {
            const a = anchors[next - 1], b = anchors[next];
            const r = b.v / moved(a, b.q);
            return {q, v:moved(a, q) * Math.pow(r, (i - a.i) / (b.i - a.i)), sale};
        }
        const a = anchors[anchors.length - 1];
        // With no model value the sale alone carries on, moved by the area price
        const wb = modelPpsm ? Math.exp(-(i - a.i) / 4 / SALE_WEIGHT_YEARS) : 1;
        saleWeight = wb;
        return {q, v:Math.exp(wb * Math.log(moved(a, q)) + (1 - wb) * Math.log(base(q))), sale};
    });
    const last = points[points.length - 1];
    const yearAgo = points.length > 4 ? points[points.length - 5].v : null;
    // The model's likely range, scaled to today's value
    const range = modelPpsm && model.lo && model.hi
        ? {lo:last.v * Number(model.lo) / modelPpsm, hi:last.v * Number(model.hi) / modelPpsm} : null;
    return {
        size, sizeFromSale:!override && !certArea && !!(lastSale && lastSale.area), sizeFromListing:!!override, kind:flat ? 'flats' : 'houses',
        salesUsed:anchors.length, saleWeight:anchors.length ? (saleWeight === null ? 1 : saleWeight) : null,
        points, now:last.v, nowQuarter:last.q, ppsmNow:price[last.q],
        yearChange:yearAgo ? (last.v - yearAgo) / yearAgo : null,
        plain:size * price[last.q],
        model:modelPpsm ? {ppsmNow:modelPpsm * idx[lastQ] / idx[modelQ], comps:Number(model.n) || null,
            neff:Number(model.neff) || null, value:base(lastQ)} : null,
        range,
        // Few close sales behind the model: the range is less to be trusted
        uncertain:!!(model && Number(model.neff) < 3),
    };
};

// The middle of a list of numbers
export const median = (xs) => {
    const s = xs.filter((x) => x !== null && x !== undefined).map(Number).sort((a, b) => a - b);
    if (!s.length) return null;
    const m = Math.floor(s.length / 2);
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// Lays out the chart's labels in rows above the plot so they never sit on the line:
// each label (centred on x, or right-aligned when right) goes in the first row where
// it doesn't overlap one already placed. Returns the labels with their box and row.
export const placeLabels = (labels, left, right, charW, gap = 6) => {
    const rows = [];
    return labels.map((lab) => {
        const w = lab.text.length * charW + 14;
        let x0 = lab.right ? lab.x - w : lab.x - w / 2;
        x0 = Math.max(left, Math.min(right - w, x0));
        let row = rows.findIndex((r) => r.every(([a, b]) => x0 + w + gap <= a || x0 >= b + gap));
        if (row < 0) { rows.push([]); row = rows.length - 1; }
        rows[row].push([x0, x0 + w]);
        return {...lab, x0, w, row};
    });
};

// The map's distances (the area menu's quarter mile to a mile, and the default 1207 m)
const MAP_DISTANCES = [402, 804, 1207, 1609];

// The map link showing the sales around a home as the Recent sales nearby card lists them:
// the property sales layer (s=propertySales) filtered to flats or houses (pt) sold in the
// last 12 months (pm), around the home's point, over the smallest distance that takes in
// the furthest of those sales. mapPath is the map's route (MAP_PATH in landing/Landing.js).
// from is the home's page, which the map offers to go back to (propertyBackPath).
// backPath, when given, is the page to go back to instead (a listing's page)
export const nearbySalesMapLink = (mapPath, {x, y, address, postcode, uprn}, flats, sales, backPath) => {
    const furthest = Math.max(0, ...sales.map((s) => Number(s.dist) || 0));
    const d = MAP_DISTANCES.find((m) => m >= furthest) || MAP_DISTANCES[MAP_DISTANCES.length - 1];
    const params = new URLSearchParams({x, y, d, s:'propertySales', pt:flats ? 'F' : 'houses', pm:'12', n:address});
    if (backPath) params.set('from', backPath);
    else if (postcode && uprn) params.set('from', propertyPath(postcode, uprn));
    return mapPath + '?' + params.toString();
};

// An area report link (/area-report?x=&y=&n=) as the from param carries it, rebuilt from its
// numbers and name alone; null for anything else
const areaReportBack = (from) => {
    if (from.length > 400) return null;
    const q = new URLSearchParams(from.slice(AREA_REPORT_PATH.length + 1));
    const x = Number(q.get('x')), y = Number(q.get('y'));
    if (!q.get('x') || !q.get('y') || !isFinite(x) || !isFinite(y)) return null;
    return {path:areaReportPath(x, y, q.get('n')), name:'the area report'};
};

// The page a map link came from (its from param), for the map's back chip
// (Controls/FocusAreaMapControl.js), with the home's name from n; null unless from is a
// property page, a listing's page or an area report, so the param can't send anyone elsewhere
export const propertyBackPath = (search) => {
    const params = new URLSearchParams(search);
    const from = params.get('from');
    if (from && from.startsWith(AREA_REPORT_PATH + '?')) return areaReportBack(from);
    // A home's page, or a saved listing's page (account/ListingLookup.js)
    if (!from || !(/^\/property\/[A-Za-z0-9-]{5,9}\/\d{1,15}$/.test(from) || /^\/listing\/\d{1,12}$/.test(from))) return null;
    return {path:from, name:params.get('n') || 'the property'};
};

// What kind of home it is and its tenure, for the top of its page: from its last recorded
// sale (Land Registry's property type and tenure), else the type from its latest energy
// certificate (built form and property type). Tenure isn't on certificates, so a home with
// no sale has none. Each comes with where it came from, for the page to say.
const SALE_TYPES = {D:'Detached house', S:'Semi-detached house', T:'Terraced house', F:'Flat or maisonette'};
const SALE_TENURES = {F:'Freehold', L:'Leasehold'};
export const homeKind = (property) => {
    const sales = (property && property.sales) || [];
    const certs = (property && property.certs) || [];
    const sale = sales.length ? sales[sales.length - 1] : null;
    const cert = certs.length ? certs[certs.length - 1] : null;
    let type = null, typeFrom = null;
    if (sale && SALE_TYPES[sale.proptype]) {
        type = SALE_TYPES[sale.proptype];
        typeFrom = 'its sale in ' + sale.date.slice(0, 4);
    } else if (cert && cert.ptype) {
        const ptype = cert.ptype.toLowerCase();
        const form = cert.form && cert.form !== 'Not Recorded' && !/flat|maisonette/.test(ptype) ? cert.form.toLowerCase() + ' ' : '';
        type = (form + ptype).replace(/^./, (c) => c.toUpperCase());
        typeFrom = 'its energy certificate';
    }
    const tenure = sale && SALE_TENURES[sale.tenure] ? SALE_TENURES[sale.tenure] : null;
    return {type, typeFrom, tenure, tenureFrom:tenure ? 'its sale in ' + sale.date.slice(0, 4) : null};
};

// How far a nearby sale is from the home, in metres, as British readers expect: "Same building"
// under 10 m, yards (to the nearest 10) up to a quarter of a mile, then miles to one place
export const fmtSaleDist = (m) => {
    if (m == null || !isFinite(m)) return '';
    if (m < 10) return 'Same building';
    const yards = m / 0.9144;
    if (yards < 440) return `${Math.max(10, Math.round(yards / 10) * 10)} yards`;
    const miles = Math.round(m / 1609.344 * 10) / 10;
    return miles === 1 ? '1 mile' : `${miles} miles`;
};
